/**
 * Dropship Checkbox Sync
 * ----------------------
 * Keeps the dropship checkbox in sync between cart-drawer and cart page.
 *
 * State is stored as a CART ATTRIBUTE (not per-item line item properties),
 * so toggling is a single network call regardless of how many items are in
 * the cart.
 *
 * The attribute key `Dropship` is NOT prefixed with an underscore, which
 * means it is visible to the customer at checkout, on the order confirmation
 * email, and in the Shopify admin under "Additional details" on the order.
 *
 * When the checkbox is toggled:
 *   1. Both checkboxes (drawer + cart page) update immediately in the DOM.
 *      The control is never disabled — rapid toggles are coalesced via
 *      `pendingState` and re-run after the in-flight call settles.
 *   2. ONE /cart/update.js call sets the `Dropship` attribute (and clears
 *      the legacy `_dropship_state` attribute that older versions of this
 *      script used). The same call requests the rendered cart sections via
 *      the `sections` parameter, so we get HTML back in one round-trip.
 *   3. The response is published through FoxTheme.pubsub so cart-items
 *      swaps the drawer/main-cart HTML, keeping the checkbox state in sync
 *      with the cart attribute on the next render.
 *
 * Net network calls when toggling: ONE /cart/update.js. That's it.
 */

(function () {
  'use strict';

  const SELECTOR = '[data-dropship-checkbox]';
  // Customer-visible cart attribute. Shows at checkout, in order
  // confirmation, and in the Shopify admin order view.
  const ATTR_KEY = 'Dropship';
  const ATTR_VALUE = 'Yes';
  // Legacy attribute key from earlier versions — cleared on every toggle so
  // it doesn't linger on existing customer carts.
  const LEGACY_ATTR_KEY = '_dropship_state';

  const cartUpdateUrl =
    (window.FoxTheme && FoxTheme.routes && FoxTheme.routes.cart_update_url) || '/cart/update.js';

  let isUpdating = false;
  let pendingState = null;

  function syncDomCheckboxes(checked) {
    document.querySelectorAll(SELECTOR).forEach((cb) => {
      if (cb.checked !== checked) cb.checked = checked;
    });
  }

  /**
   * Build a fetch init using the theme's helper if available, so headers
   * and credentials match what the rest of the cart code uses.
   */
  function buildFetchInit(body) {
    if (window.FoxTheme && FoxTheme.utils && typeof FoxTheme.utils.fetchConfig === 'function') {
      return Object.assign({}, FoxTheme.utils.fetchConfig(), { body: JSON.stringify(body) });
    }
    return {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(body),
    };
  }

  function gatherSectionIds() {
    const sections = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:grouped-sections', {
        bubbles: true,
        detail: { sections },
      })
    );
    return Array.from(new Set(sections.filter(Boolean)));
  }

  function publish(cart) {
    if (!cart) return;
    if (window.FoxTheme && FoxTheme.pubsub && FoxTheme.pubsub.PUB_SUB_EVENTS) {
      // cart-items.onCartUpdate will swap the CartDrawerFooter / MainCart
      // innerHTML using cart.sections, and dispatches its own `cart:updated`
      // event at the end — so no extra dispatch needed here.
      FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
        sources: 'dropship-checkbox',
        cart,
      });
    } else {
      // Fallback only if FoxTheme pubsub is unavailable
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
    }
  }

  /**
   * Apply the dropship state to the cart in a SINGLE network call:
   *   - Set the `Dropship` cart attribute (or clear it)
   *   - Clear the legacy `_dropship_state` attribute
   *   - Request rendered sections so the cart UI re-renders without an
   *     extra fetch.
   */
  async function applyDropshipState(requested) {
    const sectionIds = gatherSectionIds();

    const body = {
      attributes: {
        [ATTR_KEY]: requested ? ATTR_VALUE : '',
        [LEGACY_ATTR_KEY]: '',
      },
    };
    if (sectionIds.length > 0) {
      body.sections = sectionIds;
    }

    const res = await fetch(cartUpdateUrl, buildFetchInit(body));
    const updatedCart = await res.json();

    if (updatedCart && updatedCart.sections) {
      publish(updatedCart);
    }
  }

  async function handleChange(checkbox) {
    const requested = !!checkbox.checked;

    // Reflect immediately in the DOM (instant feedback in both views).
    // The checkbox is never disabled during the update — rapid toggles are
    // handled by the pendingState coalescing below, so the user can always
    // interact with the control.
    syncDomCheckboxes(requested);

    if (isUpdating) {
      // Coalesce rapid toggles — remember the latest desired state and let
      // the in-flight update finish, then re-run once more.
      pendingState = requested;
      return;
    }

    isUpdating = true;

    try {
      await applyDropshipState(requested);
    } catch (e) {
      console.error('[Dropship] Update failed:', e);
    } finally {
      isUpdating = false;

      if (pendingState !== null && pendingState !== requested) {
        const next = pendingState;
        pendingState = null;
        const cb = document.querySelector(SELECTOR);
        if (cb) {
          cb.checked = next;
          handleChange(cb);
        }
      } else {
        pendingState = null;
      }
    }
  }

  function bind() {
    // Use document-level delegation so newly rendered checkboxes (after cart
    // re-render) keep working without re-binding.
    document.addEventListener('change', (event) => {
      const target = event.target;
      if (target && target.matches && target.matches(SELECTOR)) {
        handleChange(target);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bind);
  } else {
    bind();
  }
})();